import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Download, Search, MessageCircle, Banknote, Trash2, FileText, TrendingUp, StickyNote } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { inr, today, fmtDate } from "@/lib/format";
import { printVendorLedger, printVendorPaymentRegister, printVendorStatement, printVendorPayoutMonthly, printPayoutTrend, printNoteVoucher } from "@/lib/print";
import { toast } from "sonner";

export default function VendorLedger() {
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [payments, setPayments] = useState([]);
  const [q, setQ] = useState("");
  const [vendorSel, setVendorSel] = useState("All");
  const [codeQ, setCodeQ] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [trendOpen, setTrendOpen] = useState(false);
  const [trendVendor, setTrendVendor] = useState("All");
  const [notesVendor, setNotesVendor] = useState(null);
  const [resubmitId, setResubmitId] = useState("");
  const [resubmitAmt, setResubmitAmt] = useState("");
  const [resubmitReason, setResubmitReason] = useState("");
  const [resubmitting, setResubmitting] = useState(false);
  const resubmitNote = async (it) => {
    setResubmitting(true);
    try {
      const r = await api.post("/notes/resubmit", { kind: "credit", id: it.id, amount: Number(resubmitAmt) || 0, reason: resubmitReason });
      const amt = Number(resubmitAmt) || 0;
      if (r.data.status === "Pending") {
        toast.warning(`Credit Note ${inr(amt)} resubmitted — awaiting admin approval`, {
          description: "Alert the admin now?",
          duration: 15000,
          action: {
            label: "WhatsApp Admin",
            onClick: async () => {
              let phone = "";
              try { phone = (await api.get("/notes/admin-alert-contact")).data.phone || ""; } catch {}
              const d = String(phone).replace(/\D/g, "");
              const amsg = [
                "*NTC — Note Resubmitted for Approval*",
                "",
                `Credit Note ${inr(amt)} resubmitted on Challan ${it.challan_no || "—"}`,
                `Vendor/Broker: ${notesVendor?.vendor || "—"}`,
                ...(resubmitReason ? [`Reason: ${resubmitReason}`] : []),
                "",
                "Please Approve/Reject in Outstanding → Approve Notes.",
              ].join("\n");
              window.open(d ? `https://wa.me/91${d.slice(-10)}?text=${encodeURIComponent(amsg)}` : `https://wa.me/?text=${encodeURIComponent(amsg)}`, "_blank");
            },
          },
        });
      } else {
        toast.success(`Credit Note ${inr(amt)} resubmitted — approved`);
      }
      setResubmitId("");
      setNotesVendor(null);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resubmit failed");
    } finally {
      setResubmitting(false);
    }
  };
  const [payoutMonth, setPayoutMonth] = useState(() => today().slice(0, 7));
  const [regFrom, setRegFrom] = useState("");
  const [regTo, setRegTo] = useState("");
  const [payForm, setPayForm] = useState({ vendor_name: "", date: "", amount: "", payment_mode: "Cash", ref_no: "", narration: "" });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const v = searchParams.get("vendor");
    if (v) setQ(v);
  }, [searchParams]);

  const loadReport = () => {
    api
      .get("/reports/vendor-ledger")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/vendor-payments").then((r) => setPayments(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    loadReport();
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPay = (vendorName) => {
    setPayForm({ vendor_name: vendorName || "", date: today(), amount: "", payment_mode: "Cash", ref_no: "", narration: "" });
    setPayOpen(true);
  };

  const savePayment = async () => {
    if (!payForm.vendor_name || !Number(payForm.amount)) {
      toast.error("Vendor and amount are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/vendor-payments", { ...payForm, amount: Number(payForm.amount) });
      toast.success("Payment recorded — balance updated");
      setPayOpen(false);
      loadReport();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save payment");
    } finally {
      setSaving(false);
    }
  };

  const deletePayment = async (id) => {
    try {
      await api.delete(`/vendor-payments/${id}`);
      toast.success("Payment removed");
      loadReport();
    } catch (e) {
      toast.error("Failed to delete payment");
    }
  };

  const vendorPayments = payments.filter((p) => p.vendor_name === payForm.vendor_name);
  const regRows = payments.filter((p) => (!regFrom || (p.date || "") >= regFrom) && (!regTo || (p.date || "") <= regTo));
  const payoutRows = [...payments.filter((p) => String(p.date || "").startsWith(payoutMonth))].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  const payoutTotal = payoutRows.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const payoutBy = (key) => {
    const m = {};
    payoutRows.forEach((p) => { const k = p[key] || "—"; m[k] = (m[k] || 0) + (Number(p.amount) || 0); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  };
  const payoutLabel = new Date(`${payoutMonth}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" }).toUpperCase();
  const trendVendors = [...new Set(payments.map((p) => p.vendor_name).filter(Boolean))].sort();
  const trendData = (() => {
    const now = new Date(`${today()}T00:00:00`);
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    const totals = {};
    payments.forEach((p) => {
      if (trendVendor !== "All" && p.vendor_name !== trendVendor) return;
      const m = String(p.date || "").slice(0, 7);
      totals[m] = (totals[m] || 0) + (Number(p.amount) || 0);
    });
    return months.map((m) => ({
      month: m,
      label: new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short" }) + " " + m.slice(2, 4),
      total: Math.round((totals[m] || 0) * 100) / 100,
    }));
  })();
  const trendChange = (() => {
    const cur = trendData[trendData.length - 1]?.total || 0;
    const prev = trendData[trendData.length - 2]?.total || 0;
    if (!prev) return null;
    return Math.round(((cur - prev) / prev) * 100);
  })();
  const payoutWhatsApp = () => {
    const msg = [
      "*NTC — Monthly Vendor Payout Report*",
      `Month: ${payoutLabel}`,
      `Payments: ${payoutRows.length} · Total Paid: ${inr(payoutTotal)}`,
      "",
      "*By Mode:*",
      ...payoutBy("payment_mode").map(([k, v]) => `• ${k}: ${inr(v)}`),
      "",
      "*By Branch:*",
      ...payoutBy("branch").map(([k, v]) => `• ${k}: ${inr(v)}`),
      "",
      "*Top Payouts:*",
      ...[...payoutRows].sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0)).slice(0, 5).map((p) => `• ${fmtDate(p.date)} ${p.vendor_name} — ${inr(p.amount)} (${p.payment_mode || "—"})`),
      "",
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const payoutExcel = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [cell(`Monthly Vendor Payout Report — ${payoutLabel}`), "Sl No.,Date,Vendor / Broker,Branch,Mode,Ref No.,Narration,Amount"];
    payoutRows.forEach((p, i) => lines.push([i + 1, p.date || "", p.vendor_name || "", p.branch || "", p.payment_mode || "", p.ref_no || "", p.narration || "", p.amount || 0].map(cell).join(",")));
    lines.push(`,,,,,,TOTAL,${payoutTotal}`);
    payoutBy("payment_mode").forEach(([k, v]) => lines.push([,,,,,, `Mode: ${k}`, v].map(cell).join(",")));
    payoutBy("branch").forEach(([k, v]) => lines.push([,,,,,, `Branch: ${k}`, v].map(cell).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ntc-vendor-payout-${payoutMonth}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const shareWhatsApp = async (r) => {
    const v = vendors.find((x) => (x.name || "").trim() === r.vendor);
    const digits = String(v?.phone || "").replace(/\D/g, "");
    if (!digits) {
      toast.error("No phone saved for this vendor in Vendor Master");
      return;
    }
    let link = "";
    try {
      const res = await api.post("/vendor-ledger-share", { vendor_name: r.vendor });
      link = `${window.location.origin}/api/public/vendor-ledger/${res.data.id}`;
    } catch (e) { /* link optional */ }
    const pending = (r.items || []).filter((it) => (Number(it.balance) || 0) > 0);
    const pLines = pending.map(
      (it, i) =>
        `${i + 1}. Ch ${String(it.challan_no || "—").replace(/^VHC-?/i, "")}${it.date ? ` dt ${fmtDate(it.date)}` : ""} · ${it.from_place || "—"} → ${it.destination || "—"} — *Pending ₹${(Number(it.balance) || 0).toLocaleString("en-IN")}*`
    );
    const msg = [
      `Dear ${r.vendor},`,
      "",
      "Your pending balance summary with Nisha Transport Corporation:",
      "",
      ...(pLines.length ? ["*Pending Challans:*", ...pLines, ""] : []),
      `Challans: ${r.challans}`,
      `Total Hire: ${inr(r.total_hire)}`,
      `Total Paid: ${inr(r.total_paid)}`,
      `*Balance Payable: ${inr(r.balance)}*`,
      link ? `\n📄 View / download full statement (PDF):\n${link}` : "",
      "",
      "— Nisha Transport Corporation",
    ].filter((l) => l !== "" || true).join("\n");
    window.open(`https://wa.me/${digits.length === 10 ? "91" + digits : digits}?text=${encodeURIComponent(msg)}`, "_blank");
    toast.success(`WhatsApp opened with pending balance summary for ${r.vendor}${pending.length ? ` (${pending.length} pending challan${pending.length > 1 ? "s" : ""})` : ""}`);
  };

  const waStatement = (r) => {
    const v = vendors.find((x) => (x.name || "").trim() === r.vendor);
    const digits = String(v?.phone || "").replace(/\D/g, "");
    const lines = (r.items || []).map(
      (it, i) =>
        `${i + 1}. Ch ${it.challan_no || "—"}${it.date ? ` dt ${fmtDate(it.date)}` : ""} · ${it.from_place || "—"} → ${it.destination || "—"} — Hire ₹${(Number(it.hire) || 0).toLocaleString("en-IN")}, Paid ₹${(Number(it.advance) || 0).toLocaleString("en-IN")}, Bal ₹${(Number(it.balance) || 0).toLocaleString("en-IN")}`
    );
    const msg = [
      "*Nisha Transport Corporation — Settlement Statement*",
      "",
      `Vendor / Broker: ${r.vendor}`,
      `Statement Date: ${fmtDate(new Date().toISOString().slice(0, 10))}`,
      "",
      ...lines,
      "",
      `Total Hire: ${inr(r.total_hire)}`,
      `Total Paid: ${inr(r.total_paid)}`,
      `*Balance Payable: ${inr(r.balance)}*`,
      "",
      "Kindly review the above statement before our settlement meeting. Thank you!",
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(digits ? `https://wa.me/${digits.length === 10 ? "91" + digits : digits}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const dateActive = !!(fromDate || toDate);
  const inRange = (d) => (!fromDate || (d || "") >= fromDate) && (!toDate || (d || "") <= toDate);
  const vendorNames = [...new Set((data?.rows || []).map((r) => r.vendor))].sort((a, b) => a.localeCompare(b));
  const rows = (data?.rows || [])
    .filter((r) => vendorSel === "All" || r.vendor === vendorSel)
    .filter((r) => !q || r.vendor.toLowerCase().includes(q.toLowerCase()))
    .map((r) => (dateActive ? { ...r, items: (r.items || []).filter((it) => inRange(it.date)) } : r))
    .filter((r) => !dateActive || r.items.length > 0);
  const totals = {
    total_hire: rows.reduce((s, r) => s + (r.total_hire || 0), 0),
    total_paid: rows.reduce((s, r) => s + (r.total_paid || 0), 0),
    balance: rows.reduce((s, r) => s + (r.balance || 0), 0),
  };
  const itemKey = (r, it) => it.id || `${r.vendor}|${it.challan_no}`;
  const flat = rows.flatMap((r) => {
    const items = r.items && r.items.length ? r.items : [null];
    return items.map((it, idx) => ({ r, it, first: idx === 0, span: items.length, key: it ? itemKey(r, it) : `${r.vendor}|pay` }));
  });
  const selectableKeys = flat.filter((f) => f.it).map((f) => f.key);
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableKeys));
  const toggleRow = (k) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const itemTotals = flat.reduce(
    (acc, f) => {
      if (f.it) {
        acc.hire += f.it.hire || 0;
        acc.advance += f.it.advance || 0;
        acc.credit_note += f.it.credit_note || 0;
        acc.vbal += f.it.balance || 0;
      }
      return acc;
    },
    { hire: 0, advance: 0, credit_note: 0, vbal: 0 }
  );
  const filteredForOutput = () => {
    if (selected.size === 0) return rows;
    return rows
      .map((r) => ({ ...r, items: (r.items || []).filter((it) => selected.has(itemKey(r, it))) }))
      .filter((r) => r.items.length > 0);
  };

  const paymentsExcel = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = ["Sl No.,Date,Vendor / Broker,Branch,Mode,Ref No.,Narration,Amount"];
    [...regRows].sort((a, b) => String(a.date || "").localeCompare(String(b.date || ""))).forEach((p, i) => lines.push([i + 1, p.date || "", p.vendor_name || "", p.branch || "", p.payment_mode || "", p.ref_no || "", p.narration || "", p.amount || 0].map(cell).join(",")));
    lines.push(`,,,,,,TOTAL,${regRows.reduce((s, p) => s + (Number(p.amount) || 0), 0)}`);
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ntc-vendor-payments-${today()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const challanNum = (v) => String(v || "").replace(/^VHC-?/i, "");

  const exportCsv = () => {
    const headers = ["Branch", "Vendor/Broker Name", "Vehicle No.", "Vehicle Type", "Challan No.", "Challan Date", "From", "Destination", "Vehicle Hire", "Vehicle Advance", "Credit Note", "Vehicle Balance", "Total Paid", "Outstanding Balance"];
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [];
    if (dateActive) lines.push(cell(`Period: ${fromDate ? fmtDate(fromDate) : "Beginning"} to ${toDate ? fmtDate(toDate) : "Today"}`));
    lines.push(headers.join(","));
    filteredForOutput().forEach((r) => {
      const items = r.items && r.items.length ? r.items : [null];
      items.forEach((it, idx) => {
        lines.push([
          it ? it.branch || "" : "",
          idx === 0 ? r.vendor : "",
          it ? it.vehicle_no || "" : "",
          it ? it.vehicle_type || "" : "",
          it ? challanNum(it.challan_no) : "",
          it ? it.date || "" : "",
          it ? it.from_place || "" : "",
          it ? it.destination || "" : "",
          it ? it.hire : "",
          it ? it.advance : "",
          it ? it.credit_note || "" : "",
          it ? it.balance : "",
          idx === 0 ? r.total_paid : "",
          idx === 0 ? r.balance : "",
        ].map(cell).join(","));
      });
    });
    lines.push(["TOTAL", "", "", "", "", "", "", "", itemTotals.hire, itemTotals.advance, itemTotals.credit_note, itemTotals.vbal, totals.total_paid, totals.balance].map(cell).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = dateActive ? `vendor-ledger-${fromDate || "beginning"}-to-${toDate || "today"}.csv` : "vendor-ledger.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div>
      <PageHeader
        title="Vendor / Broker Ledger"
        subtitle="Total hire engaged, amount paid, and outstanding balance per vendor / broker (from hire challans)."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setTrendOpen(true)} disabled={!payments.length} data-testid="payout-trend-btn">
              <TrendingUp className="mr-2 h-4 w-4" /> Payout Trend
            </Button>
            <Button variant="outline" onClick={() => setPayoutOpen(true)} disabled={!payments.length} data-testid="monthly-payout-btn">
              <FileText className="mr-2 h-4 w-4" /> Monthly Payout
            </Button>
            <Button variant="outline" onClick={() => setRegOpen(true)} disabled={!payments.length} data-testid="payment-register-btn">
              <Printer className="mr-2 h-4 w-4" /> Payment Register
            </Button>
            <Button variant="outline" onClick={() => openPay("")} data-testid="record-payment-btn">
              <Banknote className="mr-2 h-4 w-4" /> Record Payment
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="vendor-ledger-export-btn">
              <Download className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button onClick={() => printVendorLedger({ rows: filteredForOutput(), from: fromDate, to: toDate })} disabled={!rows.length} data-testid="vendor-ledger-print-btn">
              <Printer className="mr-2 h-4 w-4" /> {selected.size > 0 ? `Print (${selected.size})` : "Print"}
            </Button>
          </div>
        }
      />

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="h-9 pl-8 text-sm" placeholder="Search vendor / broker" value={q} onChange={(e) => setQ(e.target.value)} data-testid="vendor-ledger-search" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Vendor / Broker Name</Label>
          <Select value={vendorSel} onValueChange={(v) => { setVendorSel(v); const r = (data?.rows || []).find((x) => x.vendor === v); setCodeQ(r?.code ? String(r.code).toUpperCase() : ""); }}>
            <SelectTrigger className="h-9 w-[200px] text-sm" data-testid="vendor-ledger-vendor-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All vendors</SelectItem>
              {vendorNames.map((v) => (
                <SelectItem key={v} value={v}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Vendor / Broker Code</Label>
          <Input
            className="h-9 w-[130px] text-sm uppercase"
            placeholder="Type code"
            value={codeQ}
            onChange={(e) => {
              const v = e.target.value.toUpperCase();
              setCodeQ(v);
              const m = v.trim() && (data?.rows || []).find((r) => String(r.code || "").trim().toUpperCase() === v.trim());
              if (m) setVendorSel(m.vendor);
            }}
            data-testid="vendor-ledger-code"
          />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Challan Date From</Label>
          <Input type="date" className="h-9 w-[160px] text-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="vendor-ledger-from" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-9 w-[160px] text-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="vendor-ledger-to" />
        </div>
        {dateActive && (
          <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="vendor-ledger-clear-dates">
            Clear dates
          </Button>
        )}
      </div>

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-vendor-ledger" aria-label="Select all" />
              </TableHead>
              {["Branch", "Vendor / Broker Name", "Vendor / Broker Code", "Vehicle No.", "Vehicle Type", "Challan No.", "Challan Date", "From", "Destination", "Vehicle Hire", "Vehicle Advance", "Credit Note", "Vehicle Balance", "Total Paid", "Outstanding Balance"].map((h, i) => (
                <TableHead key={h} className={`whitespace-nowrap text-[11px] uppercase tracking-[0.1em] text-slate-500 ${i > 8 ? "text-right" : ""}`}>{h}</TableHead>
              ))}
              <TableHead className="w-[56px] text-right text-[11px] uppercase tracking-[0.1em] text-slate-500">Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 9 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                  ))}
                </TableRow>
              ))}
            {data && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} className="py-12 text-center text-muted-foreground">No vendor challans found.</TableCell>
              </TableRow>
            )}
            {data &&
              flat.map((f) => (
                <TableRow key={f.key} data-testid="vendor-ledger-row" data-state={f.it && selected.has(f.key) ? "selected" : undefined}>
                  <TableCell className="w-10">
                    {f.it ? (
                      <Checkbox checked={selected.has(f.key)} onCheckedChange={() => toggleRow(f.key)} data-testid="select-vendor-challan" aria-label="Select challan" />
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm" data-testid="vendor-ledger-branch-cell">{f.it ? f.it.branch || "—" : "—"}</TableCell>
                  {f.first && (
                    <TableCell className="align-top font-medium" rowSpan={f.span}>{f.r.vendor}</TableCell>
                  )}
                  {f.first && (
                    <TableCell className="align-top text-sm font-semibold" rowSpan={f.span} data-testid="vendor-ledger-code-cell">{f.r.code || "—"}</TableCell>
                  )}
                  <TableCell className="text-sm whitespace-nowrap" data-testid="vendor-ledger-vehicle-cell">{f.it ? f.it.vehicle_no || "—" : "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="vendor-ledger-vehicle-type-cell">{f.it ? f.it.vehicle_type || "—" : "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="vendor-ledger-challan-cell">{f.it ? challanNum(f.it.challan_no) || "—" : "—"}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{f.it && f.it.date ? fmtDate(f.it.date) : "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="vendor-ledger-from-cell">{f.it ? f.it.from_place || "—" : "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="vendor-ledger-destination-cell">{f.it ? f.it.destination || "—" : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{f.it ? inr(f.it.hire) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{f.it ? inr(f.it.advance) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-amber-700" data-testid="vendor-ledger-credit-note-cell">
                    {f.it && Number(f.it.credit_note_raw ?? f.it.credit_note) ? (
                      <>
                        {inr(f.it.credit_note_raw ?? f.it.credit_note)}
                        {f.it.credit_note_status === "Pending" && <span className="block text-[10px] font-semibold text-amber-600">pending approval</span>}
                        {f.it.credit_note_status === "Rejected" && <span className="block text-[10px] text-slate-400">rejected{f.it.credit_note_reject_reason ? ` — ${f.it.credit_note_reject_reason}` : ""}</span>}
                      </>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{f.it ? inr(f.it.balance) : "—"}</TableCell>
                  {f.first && (
                    <TableCell className="align-top text-right text-sm tabular-nums text-emerald-700" rowSpan={f.span}>{inr(f.r.total_paid)}</TableCell>
                  )}
                  {f.first && (
                    <TableCell className={`align-top text-right text-sm font-semibold tabular-nums ${f.r.balance > 0 ? "text-red-600" : "text-slate-600"}`} rowSpan={f.span}>{inr(f.r.balance)}</TableCell>
                  )}
                  {f.first && (
                    <TableCell className="align-top text-right" rowSpan={f.span}>
                      <div className="flex justify-end gap-1">
                        {(f.r.items || []).some((it) => Number(it.credit_note_raw ?? it.credit_note) > 0) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-amber-600"
                            title={`Credit note history for ${f.r.vendor}`}
                            onClick={() => setNotesVendor(f.r)}
                            data-testid="vendor-notes-btn"
                          >
                            <StickyNote className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-slate-600"
                          title={`Print settlement statement for ${f.r.vendor}`}
                          onClick={() => printVendorStatement(f.r)}
                          data-testid="vendor-statement-btn"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-sky-600"
                          title={`WhatsApp settlement statement (challan-wise) to ${f.r.vendor}`}
                          onClick={() => waStatement(f.r)}
                          data-testid="vendor-statement-wa-btn"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-primary"
                          title={`Record payment to ${f.r.vendor}`}
                          onClick={() => openPay(f.r.vendor)}
                          data-testid="row-record-payment-btn"
                        >
                          <Banknote className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-emerald-600"
                          title={`WhatsApp pending balance summary to ${f.r.vendor}`}
                          onClick={() => shareWhatsApp(f.r)}
                          data-testid="vendor-whatsapp-btn"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {data && rows.length > 0 && (
              <TableRow className="bg-slate-50 font-bold hover:bg-slate-50">
                <TableCell colSpan={10}>TOTAL</TableCell>
                <TableCell className="text-right tabular-nums">{inr(itemTotals.hire)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(itemTotals.advance)}</TableCell>
                <TableCell className="text-right tabular-nums text-amber-700">{inr(itemTotals.credit_note || 0)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(itemTotals.vbal)}</TableCell>
                <TableCell className="text-right tabular-nums text-emerald-700">{inr(totals.total_paid)}</TableCell>
                <TableCell className="text-right tabular-nums text-red-600">{inr(totals.balance)}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableShell>

      <Dialog open={!!notesVendor} onOpenChange={(v) => !v && setNotesVendor(null)}>
        <DialogContent className="sm:max-w-[720px]" data-testid="vendor-notes-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Credit Note History — {notesVendor?.vendor}</DialogTitle>
          </DialogHeader>
          {notesVendor && (() => {
            const notes = (notesVendor.items || [])
              .filter((it) => Number(it.credit_note_raw ?? it.credit_note) > 0)
              .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
            let run = 0;
            return (
              <div className="max-h-[60vh] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      {["Date", "Challan", "Reason", "Amount", "Running Total", ""].map((h) => (
                        <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${h === "Amount" || h === "Running Total" ? "text-right" : ""}`}>{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notes.map((it, i) => {
                      const amt = Number(it.credit_note_raw ?? it.credit_note) || 0;
                      const counted = !it.credit_note_status || it.credit_note_status === "Approved";
                      if (counted) run += amt;
                      const editing = resubmitId === it.id;
                      return (
                        <TableRow key={i} data-testid="vendor-note-row">
                          <TableCell className="text-sm whitespace-nowrap">{it.date ? fmtDate(it.date) : "—"}</TableCell>
                          <TableCell className="text-sm">{String(it.challan_no || "—").replace(/^VHC[-\s]?/i, "")}</TableCell>
                          <TableCell className="text-sm">
                            {editing ? (
                              <Input className="h-7 w-[170px] text-xs" placeholder="Reason" value={resubmitReason} onChange={(e) => setResubmitReason(e.target.value)} data-testid="resubmit-reason-input" />
                            ) : (
                              <>
                                {it.credit_note_reason || "—"}
                                {it.credit_note_status === "Pending" && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">PENDING</span>}
                                {it.credit_note_status === "Rejected" && (
                                  <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">REJECTED{it.credit_note_reject_reason ? ` — ${it.credit_note_reject_reason}` : ""}</span>
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm tabular-nums text-amber-700">
                            {editing ? (
                              <Input type="number" className="h-7 w-[90px] text-right text-xs" value={resubmitAmt} onChange={(e) => setResubmitAmt(e.target.value)} data-testid="resubmit-amount-input" />
                            ) : (
                              inr(amt)
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm font-semibold tabular-nums">{counted ? inr(run) : "—"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {editing ? (
                                <>
                                  <Button size="sm" className="h-7 bg-amber-600 px-2.5 text-xs text-white hover:bg-amber-700" disabled={resubmitting || !Number(resubmitAmt)} onClick={() => resubmitNote(it)} data-testid="confirm-resubmit-btn">
                                    {resubmitting ? "…" : "Resubmit"}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setResubmitId("")} data-testid="cancel-resubmit-btn">✕</Button>
                                </>
                              ) : (
                                <>
                                  {it.credit_note_status === "Rejected" && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 border-amber-300 px-2 text-xs text-amber-700 hover:bg-amber-50"
                                      title="Fix & resubmit this rejected note for approval"
                                      onClick={() => { setResubmitId(it.id); setResubmitAmt(String(amt)); setResubmitReason(it.credit_note_reason || ""); }}
                                      data-testid="resubmit-note-btn"
                                    >
                                      Resubmit
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-amber-600"
                                    title="Print A5 Credit Note voucher"
                                    onClick={() => printNoteVoucher({ date: it.date, party: notesVendor.vendor, ref: it.challan_no, amount: amt, reason: it.credit_note_reason, branch: it.branch }, "credit")}
                                    data-testid="vendor-note-voucher-btn"
                                  >
                                    <Printer className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-slate-50 font-bold hover:bg-slate-50">
                      <TableCell colSpan={3}>TOTAL APPROVED CREDIT NOTES</TableCell>
                      <TableCell className="text-right tabular-nums text-amber-700" data-testid="vendor-notes-total">{inr(run)}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableBody>
                </Table>
                <p className="mt-2 text-xs text-muted-foreground">Every deduction note issued to this vendor/broker, oldest first, with a running total — for quick dispute settlement.</p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={trendOpen} onOpenChange={setTrendOpen}>
        <DialogContent className="sm:max-w-[720px]" data-testid="payout-trend-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Vendor Payout Trend — Last 12 Months</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-3">
            <div className="grid gap-1.5">
              <Label className="text-xs">Vendor / Broker</Label>
              <Select value={trendVendor} onValueChange={setTrendVendor}>
                <SelectTrigger className="w-64" data-testid="trend-vendor-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All vendors</SelectItem>
                  {trendVendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="pt-5 text-xs text-muted-foreground" data-testid="payout-trend-summary">
              This month: <b>{inr(trendData[trendData.length - 1]?.total || 0)}</b>
              {trendChange !== null && (
                <span className={`ml-2 rounded-full px-2 py-0.5 font-bold ${trendChange > 0 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {trendChange > 0 ? "▲" : "▼"} {Math.abs(trendChange)}% vs last month
                </span>
              )}
            </p>
          </div>
          <div className="h-[300px] w-full" data-testid="payout-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={48} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={48} />
                <ChartTooltip formatter={(v) => [inr(v), "Paid"]} labelStyle={{ fontWeight: 700 }} />
                <Bar dataKey="total" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <DialogFooter>
            <Button onClick={() => printPayoutTrend(trendData, { vendorLabel: trendVendor === "All" ? "ALL VENDORS" : trendVendor })} data-testid="trend-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="sm:max-w-[760px]" data-testid="monthly-payout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Monthly Vendor Payout Report</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5">
              <Label>Month</Label>
              <Input type="month" value={payoutMonth} onChange={(e) => setPayoutMonth(e.target.value || today().slice(0, 7))} className="w-44" data-testid="payout-month" />
            </div>
            <p className="pb-2 text-xs text-muted-foreground" data-testid="payout-summary">
              {payoutRows.length} payment(s) · Total {inr(payoutTotal)}
            </p>
          </div>
          {payoutRows.length > 0 && (
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              {payoutBy("payment_mode").map(([k, v]) => (
                <span key={`m-${k}`} className="rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-700">{k}: {inr(v)}</span>
              ))}
              {payoutBy("branch").map(([k, v]) => (
                <span key={`b-${k}`} className="rounded-full bg-slate-200 px-2 py-0.5 font-semibold text-slate-700">{k}: {inr(v)}</span>
              ))}
            </div>
          )}
          <div className="max-h-[320px] overflow-auto rounded border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Vendor / Broker</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Ref No.</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payoutRows.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">No payments in {payoutLabel}.</TableCell></TableRow>
                ) : (
                  payoutRows.map((p) => (
                    <TableRow key={p.id} data-testid="payout-row">
                      <TableCell className="whitespace-nowrap text-xs">{fmtDate(p.date)}</TableCell>
                      <TableCell className="text-xs font-semibold">{p.vendor_name}</TableCell>
                      <TableCell className="text-xs">{p.branch || "—"}</TableCell>
                      <TableCell className="text-xs">{p.payment_mode || "—"}</TableCell>
                      <TableCell className="text-xs">{p.ref_no || "—"}</TableCell>
                      <TableCell className="text-right text-xs font-bold">{inr(p.amount)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={payoutWhatsApp} disabled={!payoutRows.length} data-testid="payout-wa-btn">
              <MessageCircle className="mr-2 h-4 w-4 text-emerald-600" /> WhatsApp
            </Button>
            <Button variant="outline" onClick={payoutExcel} disabled={!payoutRows.length} data-testid="payout-excel-btn">
              <Download className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button onClick={() => printVendorPayoutMonthly(payoutRows, payoutLabel)} disabled={!payoutRows.length} data-testid="payout-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle className="font-display">Vendor Payment Register</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Date From</Label>
              <Input type="date" value={regFrom} onChange={(e) => setRegFrom(e.target.value)} data-testid="register-from" />
            </div>
            <div className="grid gap-1.5">
              <Label>To</Label>
              <Input type="date" value={regTo} onChange={(e) => setRegTo(e.target.value)} data-testid="register-to" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{regRows.length} payment(s) in range · Total {inr(regRows.reduce((s, p) => s + (Number(p.amount) || 0), 0))}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={paymentsExcel} disabled={!regRows.length} data-testid="payments-excel-btn">
              <Download className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button onClick={() => printVendorPaymentRegister(regRows, { from: regFrom, to: regTo })} disabled={!regRows.length} data-testid="print-payment-register-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[520px]">
          <DialogHeader><DialogTitle className="font-display">Record Vendor Payment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 grid gap-1.5">
              <Label>Vendor / Broker</Label>
              <Select value={payForm.vendor_name} onValueChange={(v) => setPayForm((f) => ({ ...f, vendor_name: v }))}>
                <SelectTrigger data-testid="pay-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {[...new Set([...(data?.rows || []).map((r) => r.vendor), ...vendors.map((v) => v.name)])].filter(Boolean).map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Date</Label>
              <Input type="date" value={payForm.date} onChange={(e) => setPayForm((f) => ({ ...f, date: e.target.value }))} data-testid="pay-date" />
            </div>
            <div className="grid gap-1.5">
              <Label>Amount (₹)</Label>
              <Input type="number" value={payForm.amount} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} placeholder="10000" data-testid="pay-amount" />
            </div>
            <div className="grid gap-1.5">
              <Label>Mode</Label>
              <Select value={payForm.payment_mode} onValueChange={(v) => setPayForm((f) => ({ ...f, payment_mode: v }))}>
                <SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Cash", "NEFT/RTGS", "Cheque", "DD", "UPI"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Ref / UTR No.</Label>
              <Input value={payForm.ref_no} onChange={(e) => setPayForm((f) => ({ ...f, ref_no: e.target.value }))} placeholder="Optional" data-testid="pay-ref" />
            </div>
            <div className="col-span-2 grid gap-1.5">
              <Label>Narration</Label>
              <Input value={payForm.narration} onChange={(e) => setPayForm((f) => ({ ...f, narration: e.target.value }))} placeholder="Optional note" data-testid="pay-narration" />
            </div>
          </div>
          {vendorPayments.length > 0 && (
            <div className="mt-2 rounded-md border border-border bg-slate-50 p-2">
              <p className="mb-1 text-xs font-semibold text-slate-600">Earlier payments to {payForm.vendor_name}</p>
              {vendorPayments.map((p) => (
                <div key={p.id} className="flex items-center gap-2 py-0.5 text-xs" data-testid="vendor-payment-item">
                  <span>{fmtDate(p.date)}</span>
                  <span className="font-semibold">{inr(p.amount)}</span>
                  <span className="text-slate-500">{p.payment_mode}{p.ref_no ? ` · ${p.ref_no}` : ""}</span>
                  <button type="button" className="ml-auto text-destructive" title="Delete payment" onClick={() => deletePayment(p.id)} data-testid="delete-payment-btn">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={savePayment} disabled={saving} data-testid="save-payment-btn">
              {saving ? "Saving…" : "Save Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
